// An agent's transcript and system prompt, as the dashboard uses them.
//
// Since pi-agent-core 0.86 the prompt and the tools declared to the model are
// system messages at the head of `agent.state.messages`, and
// `agent.state.systemPrompt` is a read-only replay of them. The dashboard keeps
// its own model on top: one prompt, rewritten in place when a block of it
// changes; a transcript of the conversation alone, which is what is saved,
// snapshotted and synced; and a transcript assigned back onto an agent keeps
// that agent's prompt and tools rather than erasing them.

import { getCurrentSystemMessage } from "@earendil-works/pi-ai";

const isSystem = (message) => message?.role === "system";

// The prompt and tools as they stand, every later system message folded in.
function headOf(messages) {
    return getCurrentSystemMessage(messages || []) || null;
}

// The conversation without its system messages: the form the dashboard
// stores, and the one its indexes count in.
export function transcriptOf(agent) {
    return (agent?.state?.messages || []).filter((message) => !isSystem(message));
}

// Puts `messages` on `agent` behind the agent's own prompt and tools. System
// messages in `messages` are dropped: a saved transcript carries none, and one
// taken from another agent carries that agent's prompt, not this one's.
export function setTranscript(agent, messages) {
    const head = headOf(agent.state.messages);
    const conversation = (messages || []).filter((message) => !isSystem(message));
    agent.state.messages = head ? [head, ...conversation] : conversation;
}

// Replaces the prompt's text, keeping the tools declared with it and the
// conversation after it. Later system messages fold into the new head, so the
// transcript still starts with exactly one.
export function setSystemPrompt(agent, text) {
    const current = headOf(agent.state.messages);
    const conversation = transcriptOf(agent);
    const { sections: _sections, ...head } = current || { role: "system", timestamp: 0 };
    const prompt = String(text ?? "");
    agent.state.messages = prompt || head.toolsAdded?.length
        ? [{ ...head, role: "system", content: prompt }, ...conversation]
        : conversation;
}

// A sub-agent's transcript shown on a view of it (a slot, a batch item): the
// conversation alone, re-read as the child runs. The view used to hold the
// child's own array, which now leads with that child's prompt and tool
// schemas, and views are saved with the chat and synced to the server.
const MIRRORED = Symbol("mirroredAgent");
export function mirrorTranscript(view, child) {
    if (!view || !child) return;
    Object.defineProperty(view, MIRRORED, { value: child, enumerable: false, configurable: true, writable: true });
    view.messages = transcriptOf(child);
}
export function refreshMirror(view) {
    const child = view?.[MIRRORED];
    if (child) view.messages = transcriptOf(child);
}
